import { useState, useEffect, useRef } from "react";
import usePermission from "../hooks/usePermission";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  getProjects, createProjectApi, updateProjectApi,
  deleteProjectApi, deleteProjectFileApi,
} from "../api/api";
import toast from "react-hot-toast";
import FieldToast from "../components/FieldToast";
import { LoadingState } from "../components/LoadingState";
import "./Projects.css";

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────
const STATUS_LABEL = {
  "todo":        "Pending",
  "in progress": "Active",
  "suspended":   "Suspended",
  "done":        "Completed",
};

const PRIORITY_LABEL = {
  "low":    "Low",
  "medium": "Medium",
  "high":   "High",
};

const UPLOAD_CATEGORIES = [
  { label: "🖼️ Image",              value: "image",      accept: "image/*" },
  { label: "📎 General Attachment", value: "attachment",  accept: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar" },
];

const EMPTY_FORM = {
  title: "", description: "",
  status: "todo", priority: "medium", startDate: "", dueDate: "",
};

const EMPTY_FORM_ERRORS = {
  title: "", description: "", startDate: "", dueDate: "",
};

const EMPTY_CREATE_FILES = {
  photos:      [],
  attachments: [],
  photoPreviews: [],
};

function getProgress(start, end) {
  const s = new Date(start), e = new Date(end), n = new Date();
  if (n <= s) return 0;
  if (n >= e) return 100;
  return Math.round(((n - s) / (e - s)) * 100);
}

// ── Validate form ─────────────────────────────
function validateForm(formData) {
  const errors = { ...EMPTY_FORM_ERRORS };
  let valid = true;

  if (!formData.title.trim()) {
    errors.title = "Project title is required.";
    valid = false;
  } else if (formData.title.trim().length < 2) {
    errors.title = "Title must be at least 2 characters.";
    valid = false;
  }

  if (!formData.description.trim()) {
    errors.description = "Description is required.";
    valid = false;
  } else if (formData.description.trim().length < 10) {
    errors.description = "Description must be at least 10 characters.";
    valid = false;
  }

  if (formData.startDate && formData.dueDate) {
    if (new Date(formData.dueDate) < new Date(formData.startDate)) {
      errors.dueDate = "Due date must be after start date.";
      valid = false;
    }
  }

  return { errors, valid };
}

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────
export default function Projects() {
  const can = usePermission();
  const navigate = useNavigate();
  useAuth();

  const [projects,     setProjects]     = useState([]);
  const [loading,      setLoading]      = useState(true);

  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage,  setCurrentPage]  = useState(1);
  const itemsPerPage = 10;

  // Modals
  const [showCreate,  setShowCreate]  = useState(false);
  const [showEdit,    setShowEdit]    = useState(false);
  const [showDetail,  setShowDetail]  = useState(false);
  const [showUpload,  setShowUpload]  = useState(false);
  const [activeProj,  setActiveProj]  = useState(null);

  // Create / Edit form
  const [formData,    setFormData]    = useState(EMPTY_FORM);
  const [formErrors,  setFormErrors]  = useState(EMPTY_FORM_ERRORS);
  const [submitting,  setSubmitting]  = useState(false);

  // Files attached during create
  const [createFiles, setCreateFiles] = useState(EMPTY_CREATE_FILES);
  const createPhotoRef      = useRef(null);
  const createAttachRef     = useRef(null);

  // Upload (existing project)
  const [uploadFiles,    setUploadFiles]    = useState([]);
  const [uploadCategory, setUploadCategory] = useState("attachment");
  const [uploading,      setUploading]      = useState(false);
  const [dragOver,       setDragOver]       = useState(false);
  const [previews,       setPreviews]       = useState([]);
  const [previewImage,   setPreviewImage]   = useState(null);
  const fileInputRef = useRef(null);

  // Field refs for validation toasts
  const titleRef       = useRef(null);
  const descriptionRef = useRef(null);

  // Field toast state
  const [fieldToast, setFieldToast] = useState({ show: false, message: "", targetRef: null });

  const showFieldToast = (message, targetRef) => {
    setFieldToast({ show: true, message, targetRef });
    setTimeout(() => setFieldToast(prev => ({ ...prev, show: false })), 3000);
  };

  // Clear a single field error on change
  const clearError = (field) => {
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: "" }));
  };

  // ── Fetch ───────────────────────────────────
  // silent = true → refresh data without showing the full-page loader
  // (so open modals don't disappear/flicker)
  const fetchProjects = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await getProjects();
      const list =
        Array.isArray(response?.data?.data) ? response.data.data :
        Array.isArray(response?.data)        ? response.data      : [];
      setProjects(list);
    } catch (err) {
      toast.error(err?.message || "Failed to load projects");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // NOTE: fetchClients removed — this page doesn't use clients, and
  // employees get 401 "Access denied" on /client/.
  useEffect(() => { fetchProjects(); }, []);

  // ── Stats ───────────────────────────────────
  const totalFiles = Array.isArray(projects)
    ? projects.reduce((a, p) => {
        const filesCount =
          (Array.isArray(p.files)       ? p.files.length       : 0) +
          (Array.isArray(p.photos)      ? p.photos.length      : 0) +
          (Array.isArray(p.attachments) ? p.attachments.length : 0);
        return a + filesCount;
      }, 0)
    : 0;

  const activeCount    = Array.isArray(projects) ? projects.filter(p => p.status === "in progress").length : 0;
  const completedCount = Array.isArray(projects) ? projects.filter(p => p.status === "done").length        : 0;

  // ── Filter ──────────────────────────────────
  const filtered = Array.isArray(projects)
    ? projects
        .filter(p => {
          const title = String(p?.title || p?.name || "");
          return !search || title.toLowerCase().includes(search.toLowerCase());
        })
        .filter(p => !statusFilter || p.status === statusFilter)
    : [];

  // ── Pagination ──────────────────────────────
  const totalPages        = Math.ceil(filtered.length / itemsPerPage);
  const paginatedProjects = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [search, statusFilter]);

  // ── Build page numbers with ellipsis ────────
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    // always show first
    pages.push(1);
    if (currentPage > 3) pages.push("...");
    // neighbours
    const start = Math.max(2, currentPage - 1);
    const end   = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push("...");
    // always show last
    pages.push(totalPages);
    return pages;
  };

  // ── Open Edit Modal ──────────────────────────
  const openEdit = (proj) => {
    setActiveProj(proj);
    setFormData({
      title:       proj.title       || proj.name || "",
      description: proj.description || "",
      status:      proj.status      || "todo",
      priority:    proj.priority    || "medium",
      startDate:   proj.startDate   ? proj.startDate.slice(0, 10) : "",
      dueDate:     proj.dueDate     ? proj.dueDate.slice(0, 10)   : "",
    });
    setFormErrors(EMPTY_FORM_ERRORS);
    setShowEdit(true);
  };

  // ── Helpers for create-modal file picks ─────
  const addCreatePhotos = (files) => {
    const arr = Array.from(files).filter(f => f.type.startsWith("image/"));
    if (!arr.length) return;
    const newPreviews = [];
    let loaded = 0;
    arr.forEach(f => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push({ name: f.name, url: e.target.result });
        loaded++;
        if (loaded === arr.length) {
          setCreateFiles(prev => ({
            ...prev,
            photos:        [...prev.photos, ...arr],
            photoPreviews: [...prev.photoPreviews, ...newPreviews],
          }));
        }
      };
      reader.readAsDataURL(f);
    });
  };

  const addCreateAttachments = (files) => {
    const arr = Array.from(files);
    setCreateFiles(prev => ({ ...prev, attachments: [...prev.attachments, ...arr] }));
  };

  const removeCreatePhoto = (idx) => {
    setCreateFiles(prev => ({
      ...prev,
      photos:        prev.photos.filter((_, i) => i !== idx),
      photoPreviews: prev.photoPreviews.filter((_, i) => i !== idx),
    }));
  };

  const removeCreateAttachment = (idx) => {
    setCreateFiles(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== idx),
    }));
  };

  const resetCreateFiles = () => {
    setCreateFiles(EMPTY_CREATE_FILES);
    if (createPhotoRef.current)  createPhotoRef.current.value  = "";
    if (createAttachRef.current) createAttachRef.current.value = "";
  };

  // ── Create ──────────────────────────────────
  const handleCreate = async () => {
    const { errors, valid } = validateForm(formData);
    setFormErrors(errors);
    if (!valid) {
      // scroll to first error
      if (errors.title)       titleRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      else if (errors.description) descriptionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        title:       formData.title,
        description: formData.description,
        status:      formData.status,
        priority:    formData.priority  || undefined,
        startDate:   formData.startDate || undefined,
        dueDate:     formData.dueDate   || undefined,
        photos:      createFiles.photos,
        attachments: createFiles.attachments,
      };
      await createProjectApi(payload);
      toast.success("Project created successfully");
      setShowCreate(false);
      setFormData(EMPTY_FORM);
      setFormErrors(EMPTY_FORM_ERRORS);
      resetCreateFiles();
      fetchProjects();
    } catch (err) {
      toast.error(err?.message || "Failed to create project");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Update ──────────────────────────────────
  const handleUpdate = async () => {
    const { errors, valid } = validateForm(formData);
    setFormErrors(errors);
    if (!valid) {
      if (errors.title)            titleRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      else if (errors.description) descriptionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        title:       formData.title,
        description: formData.description,
        status:      formData.status,
        priority:    formData.priority  || undefined,
        startDate:   formData.startDate || undefined,
        dueDate:     formData.dueDate   || undefined,
      };
      const updated     = await updateProjectApi(activeProj._id, payload);
      const updatedProj = { ...activeProj, ...payload, ...updated };
      setProjects(prev => prev.map(p => p._id === activeProj._id ? updatedProj : p));
      toast.success("Project updated successfully");
      setShowEdit(false);
      setFormData(EMPTY_FORM);
      setFormErrors(EMPTY_FORM_ERRORS);
      setActiveProj(null);
    } catch (err) {
      toast.error(err?.message || "Failed to update project");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Delete ──────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    try {
      await deleteProjectApi(id);
      setProjects(prev => prev.filter(p => p._id !== id));
      toast.success("Project deleted successfully");
    } catch (err) {
      toast.error(err?.message || "Failed to delete project");
    }
  };

  // ── Handle file selection (upload modal) ────
  const handleFilesSelected = (files) => {
    const arr  = Array.from(files);
    setUploadFiles(arr);
    const imgs = arr.filter(f => f.type.startsWith("image/"));
    const newPreviews = [];
    let loaded = 0;
    if (!imgs.length) { setPreviews([]); return; }
    imgs.forEach(f => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push({ name: f.name, url: e.target.result });
        loaded++;
        if (loaded === imgs.length) setPreviews([...newPreviews]);
      };
      reader.readAsDataURL(f);
    });
  };

  const resetUpload = () => {
    setUploadFiles([]);
    setPreviews([]);
    setUploadCategory("attachment");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Upload files (existing project) ─────────
  const handleUpload = async () => {
    if (!uploadFiles.length) {
      showFieldToast("Please select an attachment first", fileInputRef);
      return;
    }
    try {
      setUploading(true);
      const token = localStorage.getItem("token");
      for (const file of uploadFiles) {
        const fd = new FormData();
        fd.append("attachments", file);
        fd.append("type", uploadCategory);
        const res  = await fetch(
          `${import.meta.env.VITE_API_URL || "https://api.growthstationco.com"}/projects/${activeProj._id}`,
          { method: "PATCH", headers: { Authorization: `${token}` }, body: fd }
        );
        const json = await res.json();
        if (!res.ok) throw new Error(json?.message || `Failed to upload ${file.name}`);
      }
      toast.success(`${uploadFiles.length} attachment(s) uploaded successfully`);
      resetUpload();
      setShowUpload(false);
      fetchProjects();
    } catch (err) {
      toast.error(err?.message || "Failed to upload attachments");
    } finally {
      setUploading(false);
    }
  };

  // ── Delete file ─────────────────────────────
  const handleDeleteFile = async (fileId, type) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;
    try {
      // The server only accepts: "attachment" or "photo"
      const apiType = (type === "image" || type === "photo") ? "photo" : "attachment";

      await deleteProjectFileApi(activeProj._id, fileId, apiType);

      // Remove the file from every array it might live in
      const without = (arr) => Array.isArray(arr) ? arr.filter(f => f._id !== fileId) : arr;
      const strip   = (proj) => ({
        ...proj,
        files:       without(proj.files),
        photos:      without(proj.photos),
        attachments: without(proj.attachments),
      });

      setActiveProj(prev => (prev ? strip(prev) : prev));
      setProjects(prev => prev.map(p => p._id === activeProj._id ? strip(p) : p));
      toast.success("File deleted successfully");

      // Sync with the server without flashing the full-page loader
      fetchProjects(true);
    } catch (err) {
      toast.error(err?.message || "Failed to delete file");
    }
  };

  // ── Shared Form Fields ───────────────────────
  const renderFormFields = () => (
    <>
      {/* Title */}
      <div className="form-field-wrap">
        <input
          ref={titleRef}
          placeholder="Project Title *"
          value={formData.title}
          onChange={e => {
            setFormData({ ...formData, title: e.target.value });
            clearError("title");
          }}
          className={formErrors.title ? "input-error" : ""}
        />
        {formErrors.title && (
          <span className="field-error-msg">⚠ {formErrors.title}</span>
        )}
      </div>

      {/* Description */}
      <div className="form-field-wrap">
        <textarea
          ref={descriptionRef}
          placeholder="Project Description *"
          rows={3}
          value={formData.description}
          onChange={e => {
            setFormData({ ...formData, description: e.target.value });
            clearError("description");
          }}
          style={{ resize: "vertical" }}
          className={formErrors.description ? "input-error" : ""}
        />
        {formErrors.description && (
          <span className="field-error-msg">⚠ {formErrors.description}</span>
        )}
      </div>

      {/* Priority */}
      <div className="form-field-wrap">
        <select
          value={formData.priority}
          onChange={e => setFormData({ ...formData, priority: e.target.value })}
        >
          <option value="low">Priority: Low</option>
          <option value="medium">Priority: Medium</option>
          <option value="high">Priority: High</option>
        </select>
      </div>

      {/* Status */}
      <div className="form-field-wrap">
        <select
          value={formData.status}
          onChange={e => setFormData({ ...formData, status: e.target.value })}
        >
          <option value="todo">Pending</option>
          <option value="in progress">Active</option>
          <option value="suspended">Suspended</option>
          <option value="done">Completed</option>
        </select>
      </div>

      {/* Start Date */}
      <div className="form-field-wrap date-field-wrapper">
        <label>Start Date</label>
        <input
          type="date"
          value={formData.startDate}
          onChange={e => {
            setFormData({ ...formData, startDate: e.target.value });
            clearError("startDate");
          }}
          className={formErrors.startDate ? "input-error" : ""}
        />
        {formErrors.startDate && (
          <span className="field-error-msg">⚠ {formErrors.startDate}</span>
        )}
      </div>

      {/* Due Date */}
      <div className="form-field-wrap date-field-wrapper">
        <label>Due Date</label>
        <input
          type="date"
          value={formData.dueDate}
          onChange={e => {
            setFormData({ ...formData, dueDate: e.target.value });
            clearError("dueDate");
          }}
          className={formErrors.dueDate ? "input-error" : ""}
        />
        {formErrors.dueDate && (
          <span className="field-error-msg">⚠ {formErrors.dueDate}</span>
        )}
      </div>
    </>
  );

  // ── Create modal file upload section ─────────
  const renderCreateFileSection = () => (
    <div className="create-files-section">
      <div className="create-files-title">📎 Attach Files (Optional)</div>

      {/* Photos */}
      <div className="create-file-row">
        <button type="button" className="btn-file-pick" onClick={() => createPhotoRef.current?.click()}>
          🖼️ Add Photos
        </button>
        <input
          ref={createPhotoRef}
          type="file"
          multiple
          accept="image/*"
          style={{ display: "none" }}
          onChange={e => addCreatePhotos(e.target.files)}
        />
        {createFiles.photos.length > 0 && (
          <span className="file-pick-count">{createFiles.photos.length} photo(s)</span>
        )}
      </div>

      {createFiles.photoPreviews.length > 0 && (
        <div className="create-photo-previews">
          {createFiles.photoPreviews.map((p, i) => (
            <div key={i} className="create-preview-item">
              <img src={p.url} alt={p.name} />
              <button type="button" className="preview-remove" onClick={() => removeCreatePhoto(i)}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Attachments */}
      <div className="create-file-row">
        <button type="button" className="btn-file-pick" onClick={() => createAttachRef.current?.click()}>
          📎 Add Attachments
        </button>
        <input
          ref={createAttachRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
          style={{ display: "none" }}
          onChange={e => addCreateAttachments(e.target.files)}
        />
        {createFiles.attachments.length > 0 && (
          <span className="file-pick-count">{createFiles.attachments.length} attachment(s)</span>
        )}
      </div>

      {createFiles.attachments.length > 0 && (
        <div className="create-attach-list">
          {createFiles.attachments.map((f, i) => (
            <div key={i} className="create-attach-item">
              <span>📎 {f.name}</span>
              <span className="file-size">({(f.size / 1024).toFixed(1)} KB)</span>
              <button type="button" className="preview-remove" onClick={() => removeCreateAttachment(i)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── File icon helper ─────────────────────────
  const getFileIcon = (file) => {
    if (file.type === "image" || (file.type || "").startsWith("image/")) return "🖼️";
    const name = (file.originalName || file.name || file.url || "").split("?")[0].toLowerCase();
    const ext  = name.includes(".") ? name.split(".").pop() : "";
    if (ext === "pdf")                                  return "📄";
    if (["doc", "docx"].includes(ext))                  return "📝";
    if (["xls", "xlsx", "csv"].includes(ext))           return "📊";
    if (["ppt", "pptx"].includes(ext))                  return "📽️";
    if (["mp4", "mov", "avi", "mkv"].includes(ext))     return "🎬";
    if (["zip", "rar"].includes(ext))                   return "🗜️";
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "🖼️";
    return "📎";
  };

  // ── Original file name (exactly as uploaded) ─
  const getFileName = (f) => {
    const direct = f.originalName || f.originalname || f.name;
    if (direct) return direct;
    // fallback: last part of the URL
    try {
      return decodeURIComponent((f.url || "").split("/").pop().split("?")[0]) || "Unnamed file";
    } catch {
      return "Unnamed file";
    }
  };

  // ── Uploader name (from populated object or users map) ─
  const getUploaderName = (f) => {
    const u = f.uploadedBy || f.uploader || f.user || f.createdBy;
    if (u && typeof u === "object") {
      return u.userName || u.name || u.fullName || u.username || u.email || "Unknown";
    }
    return f.uploadedByName || f.uploaderName || "Unknown";
  };

  const currentAccept = UPLOAD_CATEGORIES.find(c => c.value === uploadCategory)?.accept || "*";

  const getProjectFiles = (p) => {
    if (p.files?.length) return p.files;
    return [
      ...(p.photos      || []).map(f => ({ ...f, type: "image" })),
      ...(p.attachments || []).map(f => ({ ...f, type: "attachment" })),
    ];
  };

  const downloadFile = async (url, name) => {
    try {
      const res     = await fetch(url);
      const blob    = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, "_blank");
    }
  };

  // ─────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────
  if (loading) return <LoadingState message="Loading projects..." />;

  return (
    <div className="proj-page">

      {/* ══ HEADER ══ */}
      <div className="proj-header">
        <h1 className="proj-title">Projects</h1>
        <div className="proj-header-actions">
          {can("manageProjects") && (
            <button className="btn-add" onClick={() => { setFormData(EMPTY_FORM); setFormErrors(EMPTY_FORM_ERRORS); resetCreateFiles(); setShowCreate(true); }}>
              + New Project
            </button>
          )}
          {can("deleteProject") && (
            <button className="btn-add" onClick={() => navigate("/projects/deleted")}>
              🗑️ Deleted Projects
            </button>
          )}
        </div>
      </div>

      {/* ══ STATS ══ */}
      <div className="proj-stats">
        <div className="stat-card"><div className="stat-label">Total Projects</div><div className="stat-val">{projects.length}</div></div>
        <div className="stat-card"><div className="stat-label">Active</div><div className="stat-val">{activeCount}</div></div>
        <div className="stat-card"><div className="stat-label">Completed</div><div className="stat-val">{completedCount}</div></div>
        <div className="stat-card"><div className="stat-label">Total Files</div><div className="stat-val">{totalFiles}</div></div>
      </div>

      {/* ══ FILTERS ══ */}
      <div className="proj-filters">
        <input
          placeholder="Search by project name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="todo">Pending</option>
          <option value="in progress">Active</option>
          <option value="suspended">Suspended</option>
          <option value="done">Completed</option>
        </select>
      </div>

      {/* ══ GRID ══ */}
      <div className="proj-grid">
        {filtered.length === 0 ? (
          <div className="proj-empty">No matching projects found</div>
        ) : (
          paginatedProjects.map(p => {
            const allFiles = getProjectFiles(p);
            const prog     = p.startDate && p.dueDate ? getProgress(p.startDate, p.dueDate) : null;
            const images   = allFiles.filter(f => f.type === "image");
            return (
              <div key={p._id} className="proj-card">
                <div className="proj-card-top">
                  <span className={`dept-badge dept-${p.department}`}>{p.department}</span>
                  <span className={`status-badge status-${p.status?.replace(" ", "-")}`}>{STATUS_LABEL[p.status] || p.status}</span>
                </div>

                <div className="proj-name">{p.title || p.name}</div>
                {p.description && <div className="proj-desc">{p.description}</div>}

                {p.priority && (
                  <div className="proj-priority">
                    <span className={`priority-badge priority-${p.priority}`}>
                      {PRIORITY_LABEL[p.priority] || p.priority}
                    </span>
                  </div>
                )}

                <div className="proj-dates">
                  {p.startDate && <span>From: {p.startDate?.slice(0, 10)}</span>}
                  {p.dueDate   && <span>Until: {p.dueDate?.slice(0, 10)}</span>}
                </div>

                {prog !== null && (
                  <div className="proj-progress">
                    <div className="progress-info"><span>Progress</span><span>{prog}%</span></div>
                    <div className="progress-bar"><div className="progress-fill" style={{ width: `${prog}%` }} /></div>
                  </div>
                )}

                {images.length > 0 && (
                  <div className="proj-thumbnails">
                    {images.slice(0, 4).map((f, idx) => (
                      <img
                        key={f._id || idx}
                        src={f.url}
                        alt={getFileName(f)}
                        className="proj-thumb"
                        style={{ cursor: "pointer" }}
                        onClick={() => setPreviewImage(f.url)}
                      />
                    ))}
                    {images.length > 4 && (
                      <span className="proj-thumb-more">+{images.length - 4}</span>
                    )}
                  </div>
                )}

                <div className="proj-files-count">📎 {allFiles.length} attached file(s)</div>

                <div className="proj-actions">
                  <button className="btn-view" onClick={() => { setActiveProj({ ...p, files: allFiles }); setShowDetail(true); }}>
                    View Details
                  </button>

                  {/* Upload File — visible to everyone */}
                  <button className="btn-upload" onClick={() => { setActiveProj(p); resetUpload(); setShowUpload(true); }}>
                    Upload File
                  </button>

                  {/* Edit — still permission-based */}
                  {can("manageProjects") && (
                    <button className="btn-edit" onClick={() => openEdit(p)}>Edit</button>
                  )}

                  {/* Delete — visible to everyone */}
                  <button className="btn-del" onClick={() => handleDelete(p._id)}>Delete</button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ══ PAGINATION ══ */}
      {totalPages > 1 && (
        <div className="pagination-controls">
          {/* Prev */}
          <button
            className="pagination-btn pagination-arrow"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            ‹
          </button>

          {/* Page numbers */}
          {getPageNumbers().map((page, idx) =>
            page === "..." ? (
              <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
            ) : (
              <button
                key={page}
                className={`pagination-btn pagination-num ${currentPage === page ? "pagination-active" : ""}`}
                onClick={() => setCurrentPage(page)}
                aria-label={`Page ${page}`}
                aria-current={currentPage === page ? "page" : undefined}
              >
                {page}
              </button>
            )
          )}

          {/* Next */}
          <button
            className="pagination-btn pagination-arrow"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            ›
          </button>

          <span className="pagination-summary">
            {((currentPage - 1) * itemsPerPage) + 1}–{Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length}
          </span>
        </div>
      )}

      {/* ══ CREATE MODAL ══ */}
      {showCreate && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowCreate(false)}>
          <div className="modal-box">
            <h2>New Project</h2>
            {renderFormFields()}
            {renderCreateFileSection()}
            <div className="modal-actions">
              <button onClick={() => { setShowCreate(false); resetCreateFiles(); setFormErrors(EMPTY_FORM_ERRORS); }}>Cancel</button>
              <button className="btn-confirm" onClick={handleCreate} disabled={submitting}>
                {submitting ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ EDIT MODAL ══ */}
      {showEdit && activeProj && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowEdit(false)}>
          <div className="modal-box">
            <h2>Edit Project</h2>
            {renderFormFields()}
            <div className="modal-actions">
              <button onClick={() => { setShowEdit(false); setFormData(EMPTY_FORM); setFormErrors(EMPTY_FORM_ERRORS); }}>Cancel</button>
              <button className="btn-confirm" onClick={handleUpdate} disabled={submitting}>
                {submitting ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ DETAIL MODAL ══ */}
      {showDetail && activeProj && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowDetail(false)}>
          <div className="modal-box modal-wide">
            <h2>{activeProj.title || activeProj.name}</h2>

            <div className="detail-info">
              {activeProj.description && (
                <span style={{ gridColumn: "1/-1" }}><b>Description:</b> {activeProj.description}</span>
              )}
              <span><b>Status:</b> {STATUS_LABEL[activeProj.status] || activeProj.status}</span>
              {activeProj.priority && (
                <span><b>Priority:</b> {PRIORITY_LABEL[activeProj.priority] || activeProj.priority}</span>
              )}
              {activeProj.startDate && <span><b>From:</b>  {activeProj.startDate?.slice(0, 10)}</span>}
              {activeProj.dueDate   && <span><b>Until:</b> {activeProj.dueDate?.slice(0, 10)}</span>}
              {activeProj.startDate && activeProj.dueDate && (
                <span><b>Progress:</b> {getProgress(activeProj.startDate, activeProj.dueDate)}%</span>
              )}
            </div>

            {activeProj.startDate && activeProj.dueDate && (
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${getProgress(activeProj.startDate, activeProj.dueDate)}%` }} />
              </div>
            )}

            {activeProj.files?.some(f => f.type === "image") && (
              <>
                <div className="section-title">
                  🖼️ Images ({activeProj.files.filter(f => f.type === "image").length})
                </div>
                <div className="detail-images-grid">
                  {activeProj.files.filter(f => f.type === "image").map(f => (
                    <div key={f._id} className="detail-image-card">
                      <div className="detail-image-wrap">
                        <img
                          src={f.url}
                          alt={getFileName(f)}
                          className="detail-image"
                          style={{ cursor: "pointer" }}
                          onClick={() => setPreviewImage(f.url)}
                        />
                        <button className="img-del-btn" onClick={() => handleDeleteFile(f._id, f.type)}>✕</button>
                      </div>
                      <div className="detail-file-name" title={getFileName(f)}>{getFileName(f)}</div>
                      <div className="detail-file-uploader">👤 {getUploaderName(f)}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {activeProj.files?.some(f => f.type !== "image") && (
              <>
                <div className="section-title">
                  📎 Attachments ({activeProj.files.filter(f => f.type !== "image").length})
                </div>
                <table className="files-table">
                  <thead>
                    <tr><th>File</th><th>Type</th><th>Uploaded By</th><th>Download</th><th>Delete</th></tr>
                  </thead>
                  <tbody>
                    {activeProj.files.filter(f => f.type !== "image").map(f => (
                      <tr key={f._id}>
                        <td className="file-name-cell" title={getFileName(f)}>
                          {getFileIcon(f)} {getFileName(f)}
                        </td>
                        <td><span className="file-type-badge">{f.type}</span></td>
                        <td>👤 {getUploaderName(f)}</td>
                        <td>
                          <button className="btn-download" onClick={() => downloadFile(f.url, getFileName(f))}>
                            Download
                          </button>
                        </td>
                        <td>
                          <button
                            className="btn-del"
                            style={{ padding: "3px 10px", fontSize: "11px" }}
                            onClick={() => handleDeleteFile(f._id, f.type)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {!activeProj.files?.length && <p className="no-files">No files attached yet</p>}

            <div className="modal-actions">
              <button onClick={() => setShowDetail(false)}>Close</button>

              {can("manageProjects") && (
                <button className="btn-edit" onClick={() => { setShowDetail(false); openEdit(activeProj); }}>
                  Edit Project
                </button>
              )}

              <button className="btn-confirm" onClick={() => { setShowDetail(false); resetUpload(); setShowUpload(true); }}>
                Upload New File
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ UPLOAD MODAL ══ */}
      {showUpload && activeProj && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowUpload(false)}>
          <div className="modal-box">
            <h2>Upload Files — {activeProj.title || activeProj.name}</h2>

            <div className="upload-category-grid">
              {UPLOAD_CATEGORIES.map(cat => (
                <button
                  key={cat.value}
                  className={`upload-cat-btn ${uploadCategory === cat.value ? "active" : ""}`}
                  onClick={() => {
                    setUploadCategory(cat.value);
                    setUploadFiles([]);
                    setPreviews([]);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            <div
              className={`drop-zone ${dragOver ? "drag-over" : ""}`}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); handleFilesSelected(e.dataTransfer.files); }}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploadFiles.length === 0 ? (
                <>
                  <div className="drop-icon">☁️</div>
                  <div className="drop-text">Drag files here or click to browse</div>
                  <div className="drop-sub">You can upload multiple files at once</div>
                </>
              ) : (
                <div className="drop-text">✅ {uploadFiles.length} file(s) selected — click to change</div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={currentAccept}
                style={{ display: "none" }}
                onChange={e => handleFilesSelected(e.target.files)}
              />
            </div>

            {previews.length > 0 && (
              <div className="upload-previews">
                {previews.map((p, i) => (
                  <div key={i} className="upload-preview-item">
                    <img src={p.url} alt={p.name} />
                    <span>{p.name}</span>
                  </div>
                ))}
              </div>
            )}

            {uploadFiles.length > 0 && previews.length === 0 && (
              <div className="upload-file-list">
                {uploadFiles.map((f, i) => (
                  <div key={i} className="upload-file-item">
                    <span>{getFileIcon(f)}</span>
                    <span>{f.name}</span>
                    <span className="file-size">({(f.size / 1024).toFixed(1)} KB)</span>
                  </div>
                ))}
              </div>
            )}

            <div className="modal-actions">
              <button onClick={() => { setShowUpload(false); resetUpload(); }}>Cancel</button>
              <button className="btn-confirm" onClick={handleUpload} disabled={uploading || !uploadFiles.length}>
                {uploading ? "Uploading..." : `Upload${uploadFiles.length ? ` ${uploadFiles.length} file(s)` : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* IMAGE PREVIEW OVERLAY */}
      {previewImage && (
        <div
          className="media-overlay"
          onClick={() => setPreviewImage(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
          }}
        >
          <img
            src={previewImage}
            style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: "8px" }}
            alt="preview"
            onClick={e => e.stopPropagation()}
          />
          <button
            onClick={() => setPreviewImage(null)}
            style={{
              position: "absolute", top: "20px", right: "20px",
              background: "none", border: "none", color: "#fff",
              fontSize: "28px", cursor: "pointer",
            }}
          >✕</button>
        </div>
      )}

      <FieldToast
        message={fieldToast.message}
        targetRef={fieldToast.targetRef}
        show={fieldToast.show}
      />
    </div>
  );
}